import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { ComplaintsFormComponent } from '../components/complaints-form/complaints-form.component';
import { ComplaintsSpeechComponent } from '../components/complaints-speech/complaints-speech.component';
import { FormNotesCustomersComponent } from '../../customers/components/notes-customers-form/notes-customers-form.component';
import { ComplaintsService } from '../../../services/api/complaints.service';
import { AlertNotificationService } from '../../../services/alert-notification.service';
import { HttpParams } from '@angular/common/http';
import { MainLayoutService } from '../../../services/main-layout.service';
import { OptionService } from '../../../services/api/option.service';
import { SelectFieldComponent } from '../../../components/form/select-field/select-field.component';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectOption } from '../../../components/form/select-field/select-field.config';
import { LoaderService } from '../../../services/loader.service';
import { LoaderComponent } from '../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../components/widgets/loader/loader.config';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

export interface ComplaintStatus {
  key: string;
  label: string;
  icon: string;
  count?: number;
}

@Component({
  selector: 'app-complaints-edit-page',
  imports: [
    ComplaintsFormComponent,
    ComplaintsSpeechComponent,
    FormNotesCustomersComponent,
    SelectFieldComponent,
    ReactiveFormsModule,
    LoaderComponent
],
  templateUrl: './complaints-edit-page.component.html',
  styleUrl: './complaints-edit-page.component.scss'
})
export class ComplaintsEditPageComponent implements OnInit, OnDestroy {
  complaintId?: number;
  complaint?: any;
  complaintNotes?: any;
  isEditMode: boolean = false;
  showCloseConfirmation: boolean = false;
  showCloseComplaintDialog: boolean = false;
  canCloseComplaint: boolean = false;
  isCustomerProfile: boolean = false;
  monitorControl: FormControl = new FormControl('',);
  monitorOptions: SelectOption[] = [];
  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };
  private readonly LOADER_CONTEXT = 'complaints-edit-page';
  private destroy$ = new Subject<void>();

  statusFilters: ComplaintStatus[] = [
    { key: 'OPEN', label: 'Open', icon: 'fa-gavel', count: 0 },
    { key: 'WAITING_FOR_CUSTOMER', label: 'Waiting for customer', icon: 'fa-hourglass-half', count: 0 },
    { key: 'CLOSED', label: 'Closed', icon: 'fa-check', count: 0 },
    { key: 'WAITING_TAKING_CARE', label: 'Waiting taking care', icon: 'fa-clock-o', count: 0 },
    { key: 'WAITING_FOR_OPERATOR', label: 'Waiting for operator', icon: 'fa-user-clock', count: 0 },
    { key: 'CANCELLED', label: 'Cancelled', icon: 'fa-times', count: 0 }
  ];

  @ViewChild('closeComplaintModalContent') private closeComplaintModalContent!: TemplateRef<unknown>;
  private closeComplaintModalRef?: NgbModalRef;

  @ViewChild(ComplaintsFormComponent) complaintsFormComponent!: ComplaintsFormComponent;
  @ViewChild(ComplaintsSpeechComponent) complaintsSpeechComponent!: ComplaintsSpeechComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    protected complaintsService: ComplaintsService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private optionService: OptionService,
    private loaderService: LoaderService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    const params = new HttpParams().set('size', '9999');
    const id = this.route.snapshot.paramMap.get('complaintId');

    if (id) {
      this.complaintId = Number(id);
      this.isEditMode = true;
      this.loadComplaint(this.complaintId);
      this.loadNotes(this.complaintId, params);
    }

    this.checkUserProfile();

    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMonitorOptions(): void {
    this.optionService.complaintMonitor().subscribe({
      next: (response: any) => {
        if (response && response.data && response.data.options) {
          this.monitorOptions = response.data.options;
        }
      },
      error: (error: any) => {
        console.error('Error loading monitor options:', error);
        this.alertNotificationService.error('Failed to load monitor options');
      }
    });
  }

  private checkUserProfile(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      const customerProfiles = ['CUS1', 'CUS2', 'CUS3'];
      this.isCustomerProfile = profileCode ? customerProfiles.includes(profileCode) : false;
      
      this.canCloseComplaint = this.isCustomerProfile || this.checkInternalPermissions();
    });
  }

  private checkInternalPermissions(): boolean {
    return !this.isCustomerProfile;
  }


  private loadComplaint(complaintId: number): void {
    this.complaintsService.detail(complaintId).subscribe({
      next: (response: any) => {
        this.complaint = response.data?.complaint;
      },
      error: (error: any) => {
        console.error('Error loading complaint:', error);
        this.alertNotificationService.error('Failed to load complaint details');
      }
    });
  }

  private loadNotes(complaintId: number, params: HttpParams): void {
    this.complaintsService.listNotes(complaintId, { params }).subscribe({
      next: (response: any) => {
        if (response.status?.code === 200) {
          this.complaintNotes = response.data?.sectionNotes || [];
        }
      },
      error: (error: any) => {
        console.error('Error loading notes:', error);
      }
    });
  }

  async submitForm(): Promise<void> {
    try {
      if (!this.complaintsFormComponent || !this.complaintsFormComponent.form) {
        this.alertNotificationService.error('Form not initialized');
        return;
      }

      const formGroup = this.complaintsFormComponent.form;
      
      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      if (!formGroup.valid) {
        this.complaintsFormComponent.showAlertInfo('danger', 'Please check all required fields');
        return;
      }

      const rawValue = formGroup.getRawValue();
      
      let status = rawValue.status; 
      
      if (!this.isEditMode) {
        status = 'WAITING_TAKING_CARE';
      } else {
        const takingCareUserId = this.complaintsFormComponent.getTakingCareUserId();
        if (takingCareUserId && this.complaint?.status === 'WAITING_FOR_TAKING_CARE') {
          status = 'OPEN';
        }
      }
      
      const formData: any = {
        complaintCategoryId: Number(rawValue.complaintCategoryId),
        priority: rawValue.priority || null,
        summary: rawValue.summary,
        description: rawValue.description || null,
        status: status,
        customerPersonId: Number(rawValue.customerPersonId),
        tenantId: 1
      };
      
      if (this.isEditMode && this.complaintsFormComponent) {
        const takingCareUserId = this.complaintsFormComponent.getTakingCareUserId();
        formData.takingCareUserId = takingCareUserId;
      }

      const apiCall = this.isEditMode && this.complaintId
        ? this.complaintsService.update(this.complaintId, formData)
        : this.complaintsService.create(formData);

      this.loaderService.startLoading(this.LOADER_CONTEXT);

      apiCall.subscribe({
        next: async (response: any) => {
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
          const message = this.isEditMode ? 'Complaint updated successfully' : 'Complaint created successfully';
          this.alertNotificationService.success(message);
          
      
          const complaintId = this.isEditMode && this.complaintId 
            ? this.complaintId 
            : response.data?.complaint?.complaintId;
          
          if (complaintId && this.complaintsFormComponent.hasNewFilesToUpload()) {
            try {
              await this.complaintsFormComponent.uploadNewFiles(complaintId);
              
              if (!this.isEditMode) {
                this.router.navigate(['/app/complaints/update', complaintId]);
              } else {
                this.loadComplaint(complaintId);
              }
            } catch (fileError) {
              console.error('Error uploading files:', fileError);
              this.alertNotificationService.warning('Complaint saved but some files failed to upload');
              
              if (!this.isEditMode) {
                this.router.navigate(['/app/complaints/update', complaintId]);
              } else {
                this.loadComplaint(complaintId);
              }
            }
          } else {
            if (!this.isEditMode && complaintId) {
              this.router.navigate(['/app/complaints/update', complaintId]);
            } else if (this.complaintId) {
              this.loadComplaint(this.complaintId);
            }
          }
        },
        error: (error: any) => {
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
          console.error('Error saving complaint:', error);
          this.alertNotificationService.error('Failed to save complaint');
        }
      });
    } catch (error) {
      this.loaderService.stopLoading(this.LOADER_CONTEXT);
      console.error('Error in submitForm:', error);
      this.alertNotificationService.error('An error occurred while saving');
    }
  }

  confirmCloseComplaint(): void {
    this.monitorControl.reset();

    if (this.monitorOptions.length === 0) {
      this.loadMonitorOptions();
    }

    this.showCloseComplaintDialog = true;

    this.closeComplaintModalRef = this.modalService.open(this.closeComplaintModalContent, {
      centered: true,
      backdrop: 'static'
    });

    // Chiusura con ESC: equivale a "Cancel".
    this.closeComplaintModalRef.result.catch(() => {
      this.closeComplaintModalRef = undefined;
      if (this.showCloseComplaintDialog) {
        this.cancelCloseComplaintDialog();
      }
    });
  }

  confirmCloseComplaintWithMonitor(): void {
    if (!this.monitorControl.valid) {
      this.monitorControl.markAsTouched();
      this.alertNotificationService.warning('Please select a monitor option');
      return;
    }
    this.closeComplaint();
  }

  cancelCloseComplaintDialog(): void {
    this.dismissCloseComplaintDialog();
    this.monitorControl.reset();
  }

  private dismissCloseComplaintDialog(): void {
    this.showCloseComplaintDialog = false;
    const ref = this.closeComplaintModalRef;
    this.closeComplaintModalRef = undefined;
    ref?.dismiss();
  }

  closeComplaint(): void {
    if (!this.complaintId) return;

    const closeData: any = {
      monitor: this.monitorControl.value || null
    };

    this.complaintsService.closedComplaint(this.complaintId, closeData).subscribe({
      next: (response: any) => {
        this.alertNotificationService.success('Complaint closed successfully');
        this.showCloseConfirmation = false;
        this.dismissCloseComplaintDialog();
        this.monitorControl.reset();
        this.loadComplaint(this.complaintId!);
        this.router.navigate(['/app/complaints']);
      },
      error: (error: any) => {
        console.error('Error closing complaint:', error);
        this.alertNotificationService.error('Failed to close complaint');
        this.showCloseConfirmation = false;
        this.dismissCloseComplaintDialog();
      }
    });
  }

  onComplaintFormSavedData(event: any): void {
    if (event.success && this.complaintId) {
      this.loadComplaint(this.complaintId);
    }
  }

  onTakingCareChanged(): void { 
  }

  onComplaintSpeechStatusChange(newStatus: string): void {
    if (this.complaintId) {
      this.updateComplaintStatus(newStatus);
    }
  }

  private updateComplaintStatus(newStatus: string): void {
    if (!this.complaintId || !this.complaintsFormComponent) return;

    const rawValue = this.complaintsFormComponent.form.getRawValue();
    const updateData: any = {
      complaintCategoryId: Number(rawValue.complaintCategoryId) || null,
      priority: rawValue.priority || null,
      summary: rawValue.summary,
      description: rawValue.description || null,
      status: newStatus,
      customerPersonId: Number(rawValue.customerPersonId) || null,
      tenantId: 1
    };
    
    const takingCareUserId = this.complaintsFormComponent.getTakingCareUserId();
    updateData.takingCareUserId = takingCareUserId;

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.complaintsService.update(this.complaintId, updateData).subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        console.log(`Complaint status updated to: ${newStatus}`);
        this.loadComplaint(this.complaintId!);
      },
      error: (error: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        console.error('Error updating complaint status:', error);
        this.alertNotificationService.error('Failed to update complaint status');
      }
    });
  }

  onBackClick(): void {
    this.router.navigate(['/app/complaints']);
  }

  hasMessages(): boolean {
    return Array.isArray(this.complaintNotes) && this.complaintNotes.length > 0;
  }
}
